import { useState, useCallback, useEffect } from 'react';
import { useAppDispatch } from '../store/hooks';
import { API_CONFIG } from '../config/env';

/**
 * Error types for better error handling
 */
export enum ApiErrorType {
  NETWORK = 'NETWORK',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  NOT_FOUND = 'NOT_FOUND',
  VALIDATION = 'VALIDATION',
  SERVER = 'SERVER',
  UNKNOWN = 'UNKNOWN'
}

/**
 * Custom API error class with additional metadata
 */
export class ApiError extends Error {
  type: ApiErrorType;
  status?: number;
  data?: any;

  constructor(message: string, type: ApiErrorType, status?: number, data?: any) {
    super(message);
    this.name = 'ApiError';
    this.type = type;
    this.status = status;
    this.data = data;
  }

  /**
   * Create an ApiError from a fetch Response object
   */
  static async fromResponse(response: Response): Promise<ApiError> {
    let data: any;
    try {
      data = await response.json();
    } catch (e) {
      data = null;
    }

    let type = ApiErrorType.UNKNOWN;
    let message = data?.message || 'An unknown error occurred';

    switch (response.status) {
      case 400:
        type = ApiErrorType.VALIDATION;
        message = data?.message || 'Invalid request';
        break;
      case 401:
        type = ApiErrorType.UNAUTHORIZED;
        message = 'Authentication required';
        break;
      case 403:
        type = ApiErrorType.FORBIDDEN;
        message = 'You do not have permission to perform this action';
        break;
      case 404:
        type = ApiErrorType.NOT_FOUND;
        message = 'The requested resource was not found';
        break;
      case 500:
      case 502:
      case 503:
        type = ApiErrorType.SERVER;
        message = 'A server error occurred. Please try again later.';
        break;
    }

    return new ApiError(message, type, response.status, data);
  }

  /**
   * Create a network error
   */
  static network(message = 'Network error. Please check your connection.'): ApiError {
    return new ApiError(message, ApiErrorType.NETWORK);
  }
}

/**
 * Options for API requests
 */
export interface ApiRequestOptions<P> {
  params?: P;
  onSuccess?: (data: any) => void;
  onError?: (error: ApiError) => void;
  retry?: boolean;
  retryCount?: number;
  retryDelay?: number;
}

/**
 * Interface for the useApi hook return value
 */
export interface UseApiReturn<T, P> {
  data: T | null;
  loading: boolean;
  error: ApiError | null;
  execute: (options?: ApiRequestOptions<P>) => Promise<T | null>;
  reset: () => void;
}

/**
 * A custom hook for making API calls with loading and error states
 * Includes automatic error handling, retries, and integration with Redux
 */
export function useApi<T, P = any>(apiFunction: (params: P) => Promise<T>): UseApiReturn<T, P> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const dispatch = useAppDispatch();

  // Reset error when component unmounts
  useEffect(() => {
    return () => {
      setError(null);
    };
  }, []);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setRetryAttempt(0);
  }, []);

  const execute = useCallback(
    async (options: ApiRequestOptions<P> = {}): Promise<T | null> => {
      const {
        params,
        onSuccess,
        onError,
        retry = false,
        retryCount = 3,
        retryDelay = 1000
      } = options;

      try {
        setLoading(true);
        setError(null);

        // Add a timeout to prevent hanging requests
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => {
            reject(new ApiError('Request timed out', ApiErrorType.NETWORK));
          }, API_CONFIG.TIMEOUT);
        });

        // Execute the API function with a timeout
        const result = await Promise.race([
          apiFunction(params as P),
          timeoutPromise
        ]);

        setData(result);
        setRetryAttempt(0); // Reset retry counter on success
        onSuccess?.(result);
        return result;
      } catch (err) {
        // Handle different error types
        let apiError: ApiError;

        if (err instanceof ApiError) {
          apiError = err;
        } else if (err instanceof Error) {
          apiError = new ApiError(
            err.message || 'An unknown error occurred',
            ApiErrorType.UNKNOWN
          );
        } else {
          apiError = new ApiError('An unknown error occurred', ApiErrorType.UNKNOWN);
        }

        // Set the error state
        setError(apiError);

        // Handle retry logic
        if (retry && retryAttempt < retryCount) {
          setRetryAttempt((prev) => prev + 1);
          setTimeout(() => {
            execute(options);
          }, retryDelay * Math.pow(2, retryAttempt)); // Exponential backoff
          return null;
        }

        // Call the onError callback if provided
        onError?.(apiError);

        // Special handling for unauthorized errors
        if (apiError.type === ApiErrorType.UNAUTHORIZED) {
          // Handle logout or token refresh here if needed
          // dispatch(logout());
        }

        throw apiError;
      } finally {
        setLoading(false);
      }
    },
    [apiFunction, retryAttempt, dispatch]
  );

  return { execute, data, loading, error, reset };
}

export default useApi;
